import {
  analysisSettingsSchema,
  reasonCategorySchema,
  type AnalysisSettingsView,
  type ReasonCategoryView,
  type ScorecardCriterion,
} from '@smarton-pbx/shared';
import { Check, Flag, Plus, Sparkles, Tags, Trash2, X } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { api } from '../api';
import { Field, Loading, Notice, Pill } from '../components/ui';
import { language, locale, t, tp } from '../i18n';
import { useLoad, useSubmit } from '../lib/hooks';

const languageNames = new Intl.DisplayNames([language], { type: 'language' });
const REPORT_LANGUAGES = ['en', 'lt', 'lv', 'et', 'pl', 'it'];

function CardTitle({ icon: Icon, children }: { icon: typeof Tags; children: string }) {
  return (
    <div className="card-header">
      <div className="row" style={{ gap: '0.6rem' }}>
        <Icon size={18} className="subtle" aria-hidden />
        <h2>{children}</h2>
      </div>
    </div>
  );
}

const slug = (name: string, taken: Set<string>) => {
  const base = name.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'criterion';
  let key = base;
  for (let i = 2; taken.has(key); i++) key = `${base}-${i}`;
  return key;
};

export function AnalysisSettingsCard() {
  const loaded = useLoad(api.analysisSettings);
  if (loaded.error) return <Notice kind="error">{loaded.error}</Notice>;
  if (!loaded.data) return <Loading />;
  return <AnalysisForm settings={loaded.data} onSaved={loaded.setData} />;
}

function AnalysisForm({ settings, onSaved }: { settings: AnalysisSettingsView; onSaved: (s: AnalysisSettingsView) => void }) {
  const [mode, setMode] = useState(settings.mode);
  const [model, setModel] = useState(settings.model ?? '');
  const [baseUrl, setBaseUrl] = useState(settings.baseUrl ?? '');
  const [key, setKey] = useState('');
  const [language, setLanguage] = useState(settings.language);
  const [maskBeforeSending, setMaskBeforeSending] = useState(settings.maskBeforeSending);
  const [maskTranscriptsFor, setMaskTranscriptsFor] = useState(settings.maskTranscriptsFor);
  const [minReviewSeconds, setMinReviewSeconds] = useState(String(settings.minReviewSeconds));
  const [conversationDays, setConversationDays] = useState(String(settings.conversationDays));
  const [scorecard, setScorecard] = useState<ScorecardCriterion[]>(settings.scorecard);
  const [saved, setSaved] = useState(false);
  const form = useSubmit();
  const touch = () => setSaved(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaved(false);
    const input = {
      mode,
      model: model.trim() || null,
      baseUrl: baseUrl.trim() || null,
      key: key || null,
      language,
      maskBeforeSending,
      maskTranscriptsFor,
      minReviewSeconds: Number(minReviewSeconds),
      conversationDays: Number(conversationDays),
      scorecard: scorecard.filter((c) => c.name.trim()),
    };
    const result = await form.submit(analysisSettingsSchema, input, api.updateAnalysisSettings);
    if (result) {
      onSaved(result);
      setKey('');
      setSaved(true);
    }
  }

  const used = settings.budget ? Math.min(100, Math.round((settings.usedThisMonth / settings.budget) * 100)) : 100;

  return (
    <form className="card" onSubmit={submit} noValidate>
      <CardTitle icon={Sparkles}>{t('AI call review')}</CardTitle>
      <div className="card-body stack">
        <p className="muted">
          {t(
            'After a sales call is transcribed, a language model reviews it: a summary, the likely outcome, why the sale didn’t happen and what to do differently, each point linked to the moment in the call. Off until you choose a model.',
          )}
        </p>
        <div className="form-grid">
          <Field label={t('Model')} error={form.fields.mode}>
            <select value={mode} onChange={(e) => (touch(), setMode(e.target.value as typeof mode))} data-testid="analysis-mode">
              <option value="OFF">{t('Off')}</option>
              <option value="ANTHROPIC">Claude (Anthropic)</option>
              <option value="OPENAI">ChatGPT (OpenAI)</option>
              <option value="OPENAI_COMPATIBLE">{t('Another model (OpenAI-compatible)')}</option>
            </select>
          </Field>
          {mode !== 'OFF' && (
            <Field
              label={t('Model name')}
              hint={mode === 'ANTHROPIC' ? t('Empty: {model}', { model: settings.defaultModel }) : mode === 'OPENAI' ? t('As OpenAI names it, e.g. gpt-5') : t('As the endpoint names it')}
              error={form.fields.model}
            >
              <input value={model} onChange={(e) => (touch(), setModel(e.target.value))} placeholder={mode === 'ANTHROPIC' ? settings.defaultModel : ''} spellCheck={false} data-testid="analysis-model" />
            </Field>
          )}
          {mode === 'OPENAI_COMPATIBLE' && (
            <Field label={t('Endpoint')} hint={t('e.g. {example}', { example: 'https://llm.example.com/v1' })} error={form.fields.baseUrl}>
              <input value={baseUrl} onChange={(e) => (touch(), setBaseUrl(e.target.value))} spellCheck={false} />
            </Field>
          )}
          {mode !== 'OFF' && (
            <Field label={t('API key')} hint={settings.hasKey ? t('Leave empty to keep the current one') : t('Never shown again')} error={form.fields.key}>
              <input type="password" value={key} onChange={(e) => (touch(), setKey(e.target.value))} autoComplete="new-password" placeholder={settings.hasKey ? '••••••••' : ''} />
            </Field>
          )}
        </div>
        {mode !== 'OFF' && (
          <>
            <Notice>
              {mode === 'ANTHROPIC' || mode === 'OPENAI'
                ? t('Transcripts are sent to {provider} to be reviewed, so they leave your server. Check the provider’s data terms before you turn this on.', {
                    provider: mode === 'OPENAI' ? 'OpenAI' : 'Anthropic',
                  })
                : t('Transcripts are sent to this endpoint to be reviewed. If it runs elsewhere, they leave your server.')}{' '}
              {t('Recordings are never sent.')}
            </Notice>
            <label className="checkbox">
              <input type="checkbox" checked={maskBeforeSending} onChange={(e) => (touch(), setMaskBeforeSending(e.target.checked))} data-testid="mask-before-sending" />
              {t('Hide personal data before sending: phone numbers, email and postal addresses, card and account numbers, personal codes')}
            </label>
            <div className="form-grid">
              <Field label={t('Reviews are written in')} error={form.fields.language}>
                <select value={language} onChange={(e) => (touch(), setLanguage(e.target.value))}>
                  {REPORT_LANGUAGES.map((code) => (
                    <option key={code} value={code}>
                      {languageNames.of(code)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t('Shortest call reviewed')} hint={t('Seconds')} error={form.fields.minReviewSeconds}>
                <input inputMode="numeric" value={minReviewSeconds} onChange={(e) => (touch(), setMinReviewSeconds(e.target.value))} />
              </Field>
              <Field label={t('One conversation spans')} hint={t('Days: calls with the same number this close are also reviewed together')} error={form.fields.conversationDays}>
                <input inputMode="numeric" value={conversationDays} onChange={(e) => (touch(), setConversationDays(e.target.value))} />
              </Field>
            </div>
          </>
        )}
        <Field label={t('Hide personal data in transcripts for')} hint={t('On screen. Admins always see the full text.')} error={form.fields.maskTranscriptsFor}>
          <select value={maskTranscriptsFor} onChange={(e) => (touch(), setMaskTranscriptsFor(e.target.value as typeof maskTranscriptsFor))} style={{ maxWidth: '24rem' }}>
            <option value="NONE">{t('Nobody')}</option>
            <option value="AGENTS">{t('Agents')}</option>
            <option value="NON_ADMINS">{t('Agents and region managers')}</option>
          </select>
        </Field>

        {mode !== 'OFF' && (
          <div className="stack" style={{ gap: '0.6rem' }}>
            <div>
              <div style={{ fontWeight: 560 }}>{t('Scorecard')}</div>
              <div className="subtle" style={{ fontSize: '0.85rem' }}>
                {t('Optional. Each call is scored 1–5 on these, for coaching. Changing it starts a new version; older reviews keep theirs.')}
              </div>
            </div>
            {scorecard.map((criterion, i) => (
              <div key={criterion.key} className="criterion-row">
                <input
                  value={criterion.name}
                  placeholder={t('e.g. Finds out the customer’s needs')}
                  aria-label={t('Criterion')}
                  onChange={(e) => (touch(), setScorecard(scorecard.map((c, j) => (j === i ? { ...c, name: e.target.value } : c))))}
                />
                <input
                  value={criterion.description}
                  placeholder={t('What good looks like')}
                  aria-label={t('Description')}
                  onChange={(e) => (touch(), setScorecard(scorecard.map((c, j) => (j === i ? { ...c, description: e.target.value } : c))))}
                />
                <button type="button" className="ghost icon" aria-label={criterion.name ? t('Remove {name}', { name: criterion.name }) : t('Remove the criterion')} onClick={() => (touch(), setScorecard(scorecard.filter((_, j) => j !== i)))}>
                  <Trash2 size={16} aria-hidden />
                </button>
              </div>
            ))}
            <div>
              <button
                type="button"
                className="secondary small"
                onClick={() => (touch(), setScorecard([...scorecard, { key: slug(`criterion ${scorecard.length + 1}`, new Set(scorecard.map((c) => c.key))), name: '', description: '' }]))}
                disabled={scorecard.length >= 12}
              >
                <Plus size={15} aria-hidden /> {t('Add a criterion')}
              </button>
            </div>
          </div>
        )}

        <div>
          <div style={{ fontWeight: 560 }}>{t('This month')}</div>
          <div className="budget-bar" role="img" aria-label={t('{used}% of the monthly AI budget used', { used })}>
            <span style={{ width: `${used}%` }} className={used >= 90 ? 'high' : ''} />
          </div>
          <div className="subtle" style={{ fontSize: '0.85rem' }} data-testid="ai-usage">
            {t('{used} of {budget} tokens. Reviews pause when the budget is used up; your provider sets it.', {
              used: settings.usedThisMonth.toLocaleString(locale),
              budget: settings.budget.toLocaleString(locale),
            })}
          </div>
        </div>
        {form.error && <Notice kind="error">{form.error}</Notice>}
        {saved && <Notice kind="success">{t('Saved.')}</Notice>}
      </div>
      <div className="card-footer">
        <button type="submit" disabled={form.busy} data-testid="save-analysis">
          {t('Save')}
        </button>
      </div>
    </form>
  );
}

export function CategoriesCard() {
  const categories = useLoad(api.listCategories);
  const [error, setError] = useState<string>();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const form = useSubmit();

  async function change(category: ReasonCategoryView, status: ReasonCategoryView['status']) {
    try {
      await api.updateCategory(category.id, { name: category.name, description: category.description, status });
      categories.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function add(event: FormEvent) {
    event.preventDefault();
    const result = await form.submit(reasonCategorySchema, { name, description, status: 'ACTIVE' }, api.createCategory);
    if (result) {
      setName('');
      setDescription('');
      setAdding(false);
      categories.reload();
    }
  }

  const list = categories.data ?? [];
  const suggested = list.filter((c) => c.status === 'SUGGESTED');
  const active = list.filter((c) => c.status === 'ACTIVE');

  return (
    <div className="card">
      <CardTitle icon={Tags}>{t('Reasons sales are lost')}</CardTitle>
      <div className="card-body stack">
        <p className="muted">{t('The categories reviews sort reasons and objections into. When nothing fits, the AI suggests a new one for you to accept.')}</p>
        {(error ?? categories.error) && <Notice kind="error">{error ?? categories.error}</Notice>}
        {!categories.data ? (
          <Loading />
        ) : (
          <>
            {suggested.length > 0 && (
              <div className="stack" style={{ gap: '0.4rem' }} data-testid="suggested-categories">
                <div style={{ fontWeight: 560 }}>{t('Suggested by the AI')}</div>
                {suggested.map((c) => (
                  <div key={c.id} className="category-row suggested">
                    <span className="grow">
                      <strong>{c.name}</strong> <span className="subtle">{c.description}</span>
                    </span>
                    <button type="button" className="small" onClick={() => void change(c, 'ACTIVE')} data-testid="accept-category">
                      <Check size={14} aria-hidden /> {t('Accept')}
                    </button>
                    <button type="button" className="secondary small" onClick={() => void change(c, 'REJECTED')}>
                      <X size={14} aria-hidden /> {t('Reject')}
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="category-list" data-testid="active-categories">
              {active.map((c) => (
                <div key={c.id} className="category-row">
                  <span className="grow">
                    <strong>{c.name}</strong> <span className="subtle">{c.description}</span>
                  </span>
                  {c.uses > 0 && <Pill>{tp(c.uses, '{n} use', '{n} uses')}</Pill>}
                  <button type="button" className="ghost icon" aria-label={t('Archive {name}', { name: c.name })} title={t('Archive: no longer offered to the AI')} onClick={() => void change(c, 'ARCHIVED')}>
                    <Trash2 size={15} aria-hidden />
                  </button>
                </div>
              ))}
            </div>
            {adding ? (
              <form onSubmit={add} className="form-grid" noValidate>
                <Field label={t('Name')} error={form.fields.name}>
                  <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
                </Field>
                <Field label={t('What it means')} error={form.fields.description}>
                  <input value={description} onChange={(e) => setDescription(e.target.value)} />
                </Field>
                <div className="row" style={{ gap: '0.5rem', alignSelf: 'end' }}>
                  <button type="submit" className="small" disabled={form.busy}>
                    {t('Add')}
                  </button>
                  <button type="button" className="secondary small" onClick={() => setAdding(false)}>
                    {t('Cancel')}
                  </button>
                </div>
              </form>
            ) : (
              <div>
                <button type="button" className="secondary small" onClick={() => setAdding(true)}>
                  <Plus size={15} aria-hidden /> {t('Add a category')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** What people marked wrong: material for improving the prompts. */
export function FeedbackCard() {
  const feedback = useLoad(api.analysisFeedback);
  if (!feedback.data || feedback.data.length === 0) return null;
  return (
    <div className="card">
      <CardTitle icon={Flag}>{t('Points marked wrong')}</CardTitle>
      <ul className="feedback-list">
        {feedback.data.slice(0, 20).map((f) => (
          <li key={f.id}>
            <div>
              <Pill>{f.findingKind}</Pill> {f.findingText}
            </div>
            <div className="subtle" style={{ fontSize: '0.83rem' }}>
              {f.userName ?? t('Someone')}
              {f.note ? t(': “{note}”', { note: f.note }) : ''} · {f.model} · {new Date(f.createdAt).toLocaleDateString(locale)}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
